import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { LogOut, Settings, ChevronRight } from 'lucide-react';

const ProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isThemeHovered, setIsThemeHovered] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { currentTheme, setTheme } = useTheme();

  // Charger le username depuis user_profiles
  useEffect(() => {
    if (!user) return;

    const loadUsername = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data?.username) {
          setUsername(data.username);
        }
      } catch (error) {
        console.error('Error loading username:', error);
      }
    };

    loadUsername();
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
    setIsOpen(false);
  };

  const handleThemeToggle = () => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const getInitials = () => {
    if (user?.user_metadata?.username) {
      return user.user_metadata.username.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getAvatarUrl = () => {
    return user?.user_metadata?.avatar_url || null;
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center theme-nav-text font-semibold text-sm hover:ring-2 hover:ring-[#8B5CF6]/50 transition-all focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
      >
        {getAvatarUrl() ? (
          <img
            src={getAvatarUrl()}
            alt={user?.email || 'User'}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <span>{getInitials()}</span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 theme-card-bg theme-card-border rounded-lg shadow-lg z-50 overflow-hidden">
          {/* User Info */}
          <div className="px-4 py-3 border-b theme-card-border">
            <p className="text-sm font-medium theme-text-primary truncate">
              {username || user?.user_metadata?.username || user?.email?.split('@')[0] || 'User'}
            </p>
            <p className="text-xs theme-text-secondary truncate">{user?.email}</p>
          </div>

          {/* Theme Selector - Style Supabase */}
          <div className="px-4 py-3">
            <button
              onClick={() => {
                navigate('/settings?category=appearance');
                setIsOpen(false);
              }}
              onMouseEnter={() => setIsThemeHovered(true)}
              onMouseLeave={() => setIsThemeHovered(false)}
              className="w-full flex items-center justify-between mb-2 group"
            >
              <p className="text-xs font-medium theme-text-primary uppercase tracking-wider">Theme</p>
              <ChevronRight className={`w-3 h-3 theme-text-tertiary group-hover:theme-text-primary transition-all ${isThemeHovered ? 'translate-x-0.5' : ''}`} />
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTheme('dark')}
                onMouseEnter={() => setIsThemeHovered(true)}
                onMouseLeave={() => setIsThemeHovered(false)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                  currentTheme === 'dark'
                    ? 'theme-btn-secondary theme-text-primary border-[#8B5CF6]'
                    : 'bg-transparent theme-text-tertiary hover:theme-text-secondary hover:theme-hover-button border-transparent'
                }`}
              >
                Dark
              </button>
              <button
                onClick={() => setTheme('light')}
                onMouseEnter={() => setIsThemeHovered(true)}
                onMouseLeave={() => setIsThemeHovered(false)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                  currentTheme === 'light'
                    ? 'theme-btn-secondary theme-text-primary border-[#8B5CF6]'
                    : 'bg-transparent theme-text-tertiary hover:theme-text-secondary hover:bg-gray-50 border-transparent'
                }`}
              >
                Light
              </button>
            </div>
          </div>

          {/* Settings Link */}
          <button
            onClick={() => {
              navigate('/settings');
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm theme-text-secondary hover:theme-hover-button transition-colors flex items-center gap-3"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>

          {/* Sign Out */}
          <div className="border-t theme-card-border">
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;

